import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Clock, 
  MapPin, 
  Phone, 
  CheckCircle, 
  Users,
  Award,
  Car,
  Wrench
} from 'lucide-react';

const About = () => {
  return (
    <>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Über Auto-Glass Mannheim
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Ihr vertrauensvoller Partner für professionelle Autoglas Reparatur und Austausch
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Unsere Geschichte
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Auto-Glass Mannheim wurde mit dem Ziel gegründet, Kunden in der Region 
                Mannheim, Frankfurt und Stuttgart die beste Autoglas Reparatur und den 
                professionellsten Service zu bieten.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Mit über 15 Jahren Erfahrung in der Autoglas Reparatur haben wir uns 
                als vertrauensvoller Partner für alle Arten von Autoglas Arbeiten 
                etabliert. Unser Team aus erfahrenen Technikern arbeitet mit modernster 
                Technologie und hochwertigen Materialien.
              </p>
              <p className="text-lg text-gray-600">
                Wir verstehen, dass ein beschädigtes Autoglas nicht nur ein 
                Sicherheitsrisiko darstellt, sondern auch den Wert Ihres Fahrzeugs 
                beeinträchtigt. Deshalb bieten wir schnelle, zuverlässige und 
                kostengünstige Lösungen.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gray-100 p-8 rounded-xl"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">15+</div>
                  <div className="text-gray-600">Jahre Erfahrung</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">5000+</div>
                  <div className="text-gray-600">Zufriedene Kunden</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                  <div className="text-gray-600">Notfallservice</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
                  <div className="text-gray-600">Garantie</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Werte
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Diese Grundsätze leiten unser Handeln und garantieren Ihnen die beste Qualität
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Award size={48} className="text-blue-600" />,
                title: "Qualität",
                description: "Wir verwenden nur hochwertige Materialien und arbeiten mit modernster Technologie für optimale Ergebnisse."
              },
              {
                icon: <Clock size={48} className="text-blue-600" />,
                title: "Zuverlässigkeit",
                description: "Pünktlichkeit und Zuverlässigkeit sind für uns selbstverständlich. Wir halten unsere Versprechen."
              },
              {
                icon: <Users size={48} className="text-blue-600" />,
                title: "Kundenservice",
                description: "Unser Kunde steht im Mittelpunkt. Wir bieten persönliche Beratung und individuellen Service."
              },
              {
                icon: <Shield size={48} className="text-blue-600" />,
                title: "Transparenz",
                description: "Faire und transparente Preise ohne versteckte Kosten. Sie wissen immer, was Sie erwartet."
              },
              {
                icon: <Wrench size={48} className="text-blue-600" />,
                title: "Fachkompetenz",
                description: "Unser Team verfügt über umfassende Erfahrung und regelmäßige Weiterbildungen."
              },
              {
                icon: <Car size={48} className="text-blue-600" />,
                title: "Innovation",
                description: "Wir bleiben am Puls der Zeit und setzen die neuesten Technologien und Methoden ein."
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="text-center mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unser Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Erfahrene Techniker, die Ihr Fahrzeug mit Sorgfalt und Präzision behandeln
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Michael Schmidt",
                position: "Geschäftsführer & Senior Techniker",
                experience: "15+ Jahre Erfahrung",
                specialties: ["Autoglas Reparatur", "Qualitätskontrolle", "Kundenberatung"]
              },
              {
                name: "Thomas Weber",
                position: "Senior Autoglas Techniker",
                experience: "12+ Jahre Erfahrung",
                specialties: ["Scheibenversiegelung", "Notfallreparaturen", "Mobile Service"]
              },
              {
                name: "Andreas Müller",
                position: "Autoglas Techniker",
                experience: "8+ Jahre Erfahrung",
                specialties: ["Autoglas Austausch", "Moderne Fahrzeuge", "Elektronik"]
              }
            ].map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-xl text-center"
              >
                <div className="w-24 h-24 bg-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Users size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-blue-600 font-medium mb-2">
                  {member.position}
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  {member.experience}
                </p>
                <div className="space-y-2">
                  {member.specialties.map((specialty, specialtyIndex) => (
                    <div key={specialtyIndex} className="flex items-center justify-center space-x-2 text-gray-700">
                      <CheckCircle size={14} className="text-green-500" />
                      <span className="text-sm">{specialty}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Servicegebiete
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir sind für Sie da in Mannheim, Frankfurt und Stuttgart
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                city: "Mannheim",
                description: "Autoglas Reparatur und Austausch in Mannheim und Umgebung. Zentrale Lage für schnellen Service.",
                features: ["Zentrale Werkstatt", "Mobile Reparatur", "Schneller Service", "Faire Preise"]
              },
              {
                city: "Frankfurt",
                description: "Professionelle Autoglas Reparatur in Frankfurt am Main. Wir kommen auch zu Ihnen nach Hause.",
                features: ["Mobile Service", "24/7 Notfallservice", "Originalteile", "Erfahrene Techniker"]
              },
              {
                city: "Stuttgart",
                description: "Autoglas Reparatur Stuttgart - Ihr vertrauensvoller Partner für alle Autoglas Arbeiten.",
                features: ["Komplettservice", "Günstige Preise", "Sofortige Verfügbarkeit", "Professionelle Ausführung"]
              }
            ].map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <MapPin size={24} className="text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {area.city}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {area.description}
                </p>
                <ul className="space-y-2">
                  {area.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Vertrauen Sie auf unsere Erfahrung
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Über 15 Jahre Erfahrung in der Autoglas Reparatur. Kontaktieren Sie uns 
              für ein kostenloses Angebot.
            </p>
            <a
              href="tel:062171895675"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 inline-flex items-center space-x-2"
            >
              <Phone size={24} />
              <span>Jetzt anrufen: 0621 71895675</span>
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About; 